import type { FormikProps } from "formik";
import { Formik } from "formik";
import type React from "react";
import type { ReactNode } from "react";
import { Form, FormControl } from "react-bootstrap";
import { toast } from "react-toastify";
import { FormGroup } from "~/components/common/parts/form/FormGroup";
import { FormLabel } from "~/components/common/parts/form/FormLabel";
import { Modal } from "~/components/common/parts/modal/Modal";
import { ModalFooter } from "~/components/common/parts/modal/ModalFooter";
import { useIsItandi } from "~/context/VendorSession";
import { useVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { VendorUser, VendorUserSettingParam } from "~/models/VendorUser";
import { createVendorUserValidationScheme } from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/vendors/VendorUser";

type Props = {
	handleClose: () => void;
	vendorUser?: VendorUser | null;
};

export const VendorUserModal: React.FC<Props> = ({
	vendorUser = null,
	handleClose
}: Props) => {
	const canEditSlackMemberId = useIsItandi();
	const { mutate: mutateVendorUsers } = useVendorUsers().index();
	const initialValue: VendorUserSettingParam = {
		email: vendorUser?.email ?? "",
		name: vendorUser?.name ?? "",
		slackMemberId: vendorUser?.slackMemberId ?? "",
		tel: vendorUser?.tel ?? ""
	};
	const handleSave = async (
		submitValue: VendorUserSettingParam
	): Promise<void> => {
		const payload: VendorUserSettingParam = {
			email: submitValue.email,
			name: submitValue.name,
			tel: submitValue.tel,
			...(canEditSlackMemberId
				? {
						slackMemberId:
							submitValue.slackMemberId === ""
								? null
								: submitValue.slackMemberId
					}
				: {})
		};
		const result =
			vendorUser === null
				? await create(payload)
				: await update(payload, vendorUser.id);
		if (isSuccess(result)) {
			await mutateVendorUsers?.();
			handleClose();
			toast.success("保存しました");
			return;
		}
		toast.error(result.message);
	};
	return (
		<Modal
			handleClose={handleClose}
			isOpen
			title={vendorUser === null ? "担当者追加" : "担当者編集"}
		>
			<Formik
				initialValues={initialValue}
				onSubmit={handleSave}
				validationSchema={createVendorUserValidationScheme(
					canEditSlackMemberId
				)}
			>
				{({
					values,
					handleChange,
					handleBlur,
					errors,
					isSubmitting,
					handleSubmit,
					touched
				}: FormikProps<VendorUserSettingParam>): ReactNode => (
					<div>
						<FormGroup>
							<FormLabel>担当者名</FormLabel>
							<FormControl
								isInvalid={!!(touched.name && errors.name)}
								name="name"
								onBlur={handleBlur}
								onChange={handleChange}
								value={values.name ?? ""}
							/>
							<Form.Control.Feedback type="invalid">
								{errors.name}
							</Form.Control.Feedback>
						</FormGroup>
						<FormGroup>
							<FormLabel>電話番号</FormLabel>
							<FormControl
								isInvalid={!!(touched.tel && errors.tel)}
								name="tel"
								onBlur={handleBlur}
								onChange={handleChange}
								value={values.tel ?? ""}
							/>
							<Form.Control.Feedback type="invalid">
								{errors.tel}
							</Form.Control.Feedback>
						</FormGroup>
						<FormGroup>
							<FormLabel>メールアドレス</FormLabel>
							<FormControl
								isInvalid={!!(touched.email && errors.email)}
								name="email"
								onBlur={handleBlur}
								onChange={handleChange}
								value={values.email ?? ""}
							/>
							<Form.Control.Feedback type="invalid">
								{errors.email}
							</Form.Control.Feedback>
						</FormGroup>
						{canEditSlackMemberId ? (
							<FormGroup>
								<FormLabel>Slack ID</FormLabel>
								<FormControl
									isInvalid={!!(touched.slackMemberId && errors.slackMemberId)}
									name="slackMemberId"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder="例）U0123456789"
									value={values.slackMemberId ?? ""}
								/>
								<Form.Control.Feedback type="invalid">
									{errors.slackMemberId}
								</Form.Control.Feedback>
							</FormGroup>
						) : null}
						<ModalFooter
							isSubmitting={isSubmitting}
							onClickAccept={handleSubmit}
							onClickCancel={handleClose}
						/>
					</div>
				)}
			</Formik>
		</Modal>
	);
};
